import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';
import useOpeningHours from 'gatsby-theme-core/src/hooks/use-opening-hours';

const Row = ({
  Element = 'td',
  title = '',
  value,
  values,
}) => {
  if (values) {
    if (values.every((v) => !v)) {
      return null;
    }
    return (
      <tr>
        <Element>{title}</Element>
        {values.map((val) => (
          <Element key={val}>{val}</Element>
        ))}
      </tr>
    );
  }
  if (!value) {
    return null;
  }
  return (
    <tr>
      <Element>{title}</Element>
      <Element>{value}</Element>
    </tr>
  );
};

const OpeningHoursTable = ({ openingHours, companyId }) => {
  const fallbackOpeningHours = useOpeningHours({ companyId });
  const { byLine, byDay, note } = openingHours || fallbackOpeningHours;

  if (byLine.length > 0) {
    return (
      <Wrapper>
        <table className="no-styling">
          {byLine.map(({ title, lines }, index) => (
            <Fragment key={title || `openingtijden-${index}`}>
              <thead colSpan="2">
                <tr>
                  <th>{title}</th>
                </tr>
              </thead>
              <tbody>
                {(lines || []).map((line) => (
                  <tr key={line.title}>
                    <td>{line.title}</td>
                    <td>{line.value}</td>
                  </tr>
                ))}
              </tbody>
            </Fragment>
          ))}
        </table>
        {note && <small>{note}</small>}
      </Wrapper>
    );
  }

  if (byDay.length === 2) {
    return (
      <Wrapper>
        <Table $hasTitle={byDay[0]?.title} className="no-styling">
          <thead css="text-align: left;">
            <Row
              Element="th"
              values={[byDay[0].title, byDay[1].title]}
            />
          </thead>
          <tbody>
            <Row
              values={[byDay[0].monday, byDay[1].monday]}
              title="Ma"
            />
            <Row
              values={[byDay[0].tuesday, byDay[1].tuesday]}
              title="Di"
            />
            <Row
              values={[byDay[0].wednesday, byDay[1].wednesday]}
              title="Wo"
            />
            <Row
              values={[byDay[0].thursday, byDay[1].thursday]}
              title="Do"
            />
            <Row
              values={[byDay[0].friday, byDay[1].friday]}
              title="Vr"
            />
            <Row
              values={[byDay[0].saturday, byDay[1].saturday]}
              title="Za"
            />
            <Row
              values={[byDay[0].sunday, byDay[1].sunday]}
              title="Zo"
            />
          </tbody>
        </Table>
        {note && <small>{note}</small>}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Table $hasTitle={byDay[0]?.title} className="no-styling">
        {byDay.map((daySet) => (
          <>
            <thead css="text-align: left;">
              <Row
                Element="th"
                value={daySet.title}
              />
            </thead>
            <tbody>
              <Row
                value={daySet.monday}
                title="Ma"
              />
              <Row
                value={daySet.tuesday}
                title="Di"
              />
              <Row
                value={daySet.wednesday}
                title="Wo"
              />
              <Row
                value={daySet.thursday}
                title="Do"
              />
              <Row
                value={daySet.friday}
                title="Vr"
              />
              <Row
                value={daySet.saturday}
                title="Za"
              />
              <Row
                value={daySet.sunday}
                title="Zo"
              />
            </tbody>
          </>
        ))}
      </Table>
      {note && <small>{note}</small>}
    </Wrapper>
  );
};

const Table = styled.table`
  ${({ $hasTitle }) => $hasTitle && css`
    margin-top: -1rem;
  `}

  th {
    padding-top: 1rem;
  }
`;

const Wrapper = styled.div`
  display: grid;
  gap: 1rem;
  max-width: 412px;

  > div {
    display: flex;

    span {
      flex: 1;
    }
  }
`;

export default OpeningHoursTable;
